<template>
  <div class="movements-manager">
    <!-- Barre d'outils -->
    <div class="toolbar">
      <div class="toolbar-left">
        <div class="filters">
          <div class="filter-wrapper filter-movement-type" :class="getTypeMouvementClass">
            <div class="filter-icon">
              <i class="fas fa-exchange-alt"></i>
            </div>
            <select v-model="filters.typeMouvement" @change="applyFilters" class="filter-select premium-select">
              <option value="">Tous les types</option>
              <option value="entree_achat">🚚 Entrée achat</option>
              <option value="sortie_technicien">👨‍🔧 Sortie technicien</option>
              <option value="consommation_client">👤 Consommation client</option>
              <option value="retour_non_consomme">↩️ Retour non consommé</option>
              <option value="retour_defectueux">⚠️ Retour défectueux</option>
              <option value="inventaire_ajustement">⚖️ Ajustement inventaire</option>
            </select>
            <div class="filter-arrow">
              <i class="fas fa-chevron-down"></i>
            </div>
          </div>
          
          <div class="filter-wrapper filter-movement-status" :class="getStatutClass">
            <div class="filter-icon">
              <i class="fas fa-flag-checkered"></i>
            </div>
            <select v-model="filters.statut" @change="applyFilters" class="filter-select premium-select">
              <option value="">Tous statuts</option>
              <option value="en_attente">⏳ En attente</option>
              <option value="valide">✅ Validé</option>
              <option value="annule">❌ Annulé</option>
            </select>
            <div class="filter-arrow">
              <i class="fas fa-chevron-down"></i>
            </div>
          </div>
          
          <div class="date-filter-wrapper filter-date-start" :class="getDateDebutClass">
            <div class="filter-icon">
              <i class="fas fa-calendar-alt"></i>
            </div>
            <input
              type="date"
              v-model="filters.dateDebut"
              @change="applyFilters"
              class="filter-input premium-date"
              placeholder="Date début"
            />
          </div>
          
          <div class="date-filter-wrapper filter-date-end" :class="getDateFinClass">
            <div class="filter-icon">
              <i class="fas fa-calendar-check"></i>
            </div>
            <input
              type="date"
              v-model="filters.dateFin"
              @change="applyFilters"
              class="filter-input premium-date"
              placeholder="Date fin"
            />
          </div>
        </div>
      </div>
      
      <!-- View Toggle -->
      <div class="view-toggle">
        <button 
          class="toggle-btn" 
          :class="{ active: viewMode === 'timeline' }" 
          @click="viewMode = 'timeline'"
          title="Vue timeline"
        >
          <i class="fas fa-stream"></i>
          <span>Timeline</span>
        </button>
        <button 
          class="toggle-btn" 
          :class="{ active: viewMode === 'table' }" 
          @click="viewMode = 'table'"
          title="Vue tableau"
        >
          <i class="fas fa-table"></i>
          <span>Tableau</span>
        </button>
      </div>
      
      <div class="toolbar-right">
        <button @click="exportMovements" class="btn btn-export-movements">
          <i class="fas fa-download"></i>
          Exporter
        </button>
        <button @click="$emit('create')" class="btn btn-primary">
          <i class="fas fa-plus"></i>
          Nouveau Mouvement
        </button>
      </div>
    </div>

    <!-- Statistiques rapides -->
    <div class="quick-stats" v-if="stats">
      <div class="stat-item">
        <div class="stat-icon stat-icon-blue">
          <i class="fas fa-arrow-up"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ stats.entrees }}</div>
          <div class="stat-label">Entrées</div>
        </div>
      </div>
      <div class="stat-item">
        <div class="stat-icon stat-icon-red">
          <i class="fas fa-arrow-down"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ stats.sorties }}</div>
          <div class="stat-label">Sorties</div>
        </div>
      </div>
      <div class="stat-item">
        <div class="stat-icon stat-icon-orange">
          <i class="fas fa-clock"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ stats.enAttente }}</div>
          <div class="stat-label">En attente</div>
        </div>
      </div>
      <div class="stat-item">
        <div class="stat-icon stat-icon-green">
          <i class="fas fa-euro-sign"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ formatPrice(stats.valeurTotale) }}</div>
          <div class="stat-label">Valeur totale</div>
        </div>
      </div>
    </div>

    <!-- Mouvements Table View -->
    <div v-if="viewMode === 'table'" class="movements-table-wrapper">
      <div v-if="loading" class="loading-overlay">
        <div class="loading-spinner">
          <i class="fas fa-spinner fa-spin"></i>
          <span>Chargement des mouvements...</span>
        </div>
      </div>
      
      <div v-else-if="filteredMovements.length === 0" class="no-data">
        <i class="fas fa-exchange-alt"></i>
        <h3>Aucun mouvement trouvé</h3>
        <p>Aucun mouvement ne correspond aux critères sélectionnés.</p>
      </div>
      
      <div v-else class="table-scroll">
        <table class="movements-table">
          <thead>
            <tr>
              <th>N° Mouvement</th>
              <th>Date</th>
              <th>Type</th>
              <th>Produit</th>
              <th>Quantité</th>
              <th>Valeur</th>
              <th>De</th>
              <th>Vers</th>
              <th>Technicien</th>
              <th>Statut</th>
              <th style="min-width:120px;">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="movement in paginatedMovements" :key="movement._id" :class="getMovementRowClass(movement)">
              <td>
                <div class="movement-number-cell">
                  <span class="movement-number">{{ movement.numeroMouvement }}</span>
                </div>
              </td>
              <td>
                <div class="date-cell">
                  {{ formatDate(movement.dateHeureMouvement) }}
                </div>
              </td>
              <td>
                <div class="type-cell">
                  <i :class="getMovementIcon(movement.typeMouvement)"></i>
                  {{ getMovementTitle(movement.typeMouvement) }}
                </div>
              </td>
              <td>
                <div class="product-cell">
                  <div class="product-ref">{{ movement.produit?.referenceInterne }}</div>
                  <div class="product-name">{{ movement.produit?.designation }}</div>
                </div>
              </td>
              <td>
                <div class="quantity-cell" :class="movement.quantite > 0 ? 'positive' : 'negative'">
                  {{ movement.quantite > 0 ? '+' : '' }}{{ movement.quantite }}
                </div>
              </td>
              <td>
                <div class="value-cell">
                  {{ formatPrice(movement.valeurTotale) }}
                </div>
              </td>
              <td>
                <div class="location-cell">
                  {{ formatLocation(movement.emplacementOrigine) }}
                </div>
              </td>
              <td>
                <div class="location-cell">
                  {{ formatLocation(movement.emplacementDestination) }}
                </div>
              </td>
              <td>
                <div class="technician-cell" v-if="movement.technicienConcerne || movement.technicien">
                  {{ (movement.technicienConcerne || movement.technicien)?.prenom }} {{ (movement.technicienConcerne || movement.technicien)?.nom }}
                </div>
                <span v-else class="muted">-</span>
              </td>
              <td>
                <span class="movement-status" :class="getStatusClass(movement.statut)">
                  {{ getStatusLabel(movement.statut) }}
                </span>
              </td>
              <td>
                <div class="row-actions" v-if="movement.statut === 'en_attente' || movement.typeMouvement === 'retour_non_consomme'">
                  <button v-if="movement.statut === 'en_attente'" @click="validateMovement(movement)" class="btn btn-success btn-sm" title="Valider">
                    <i class="fas fa-check"></i>
                  </button>
                  <button v-if="movement.statut === 'en_attente'" @click="cancelMovement(movement)" class="btn btn-danger btn-sm" title="Annuler">
                    <i class="fas fa-times"></i>
                  </button>
                  <button v-if="movement.typeMouvement === 'retour_non_consomme'" @click="exportRetourPDF(movement)" class="btn btn-primary btn-sm" title="Export PDF bon de retour">
                    <i class="fas fa-file-pdf"></i>
                  </button>
                </div>
                <span v-else class="muted">-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Timeline des mouvements -->
    <div v-else class="movements-timeline">
      <div v-if="loading" class="loading-overlay">
        <div class="loading-spinner">
          <i class="fas fa-spinner fa-spin"></i>
          <span>Chargement des mouvements...</span>
        </div>
      </div>
      
      <div v-else-if="filteredMovements.length === 0" class="no-data">
        <i class="fas fa-exchange-alt"></i>
        <h3>Aucun mouvement trouvé</h3>
        <p>Aucun mouvement ne correspond aux critères sélectionnés.</p>
      </div>
      
      <div v-else class="timeline">
        <div 
          v-for="movement in paginatedMovements" 
          :key="movement._id" 
          class="timeline-item"
          :class="getMovementClass(movement)"
        >
          <div class="timeline-marker">
            <i :class="getMovementIcon(movement.typeMouvement)"></i>
          </div>
          
          <div class="timeline-content">
            <div class="movement-header">
              <div class="movement-info">
                <h4 class="movement-title">{{ getMovementTitle(movement.typeMouvement) }}</h4>
                <span class="movement-number">{{ movement.numeroMouvement }}</span>
              </div>
              <div class="movement-meta">
                <span class="movement-date">{{ formatDate(movement.dateHeureMouvement) }}</span>
                <span class="movement-status" :class="getStatusClass(movement.statut)">
                  {{ getStatusLabel(movement.statut) }}
                </span>
              </div>
            </div>
            
            <div class="movement-details">
              <div class="detail-row">
                <div class="detail-item">
                  <label>Produit</label>
                  <div class="product-info">
                    <strong>{{ movement.produit?.referenceInterne }}</strong>
                    <span>{{ movement.produit?.designation }}</span>
                  </div>
                </div>
                <div class="detail-item">
                  <label>Quantité</label>
                  <div class="quantity" :class="movement.quantite > 0 ? 'positive' : 'negative'">
                    {{ movement.quantite > 0 ? '+' : '' }}{{ movement.quantite }}
                  </div>
                </div>
                <div class="detail-item">
                  <label>Valeur</label>
                  <div class="value">{{ formatPrice(movement.valeurTotale) }}</div>
                </div>
              </div>
              
              <div class="detail-row">
                <div class="detail-item">
                  <label>De</label>
                  <div class="location">{{ formatLocation(movement.emplacementOrigine) }}</div>
                </div>
                <div class="detail-item">
                  <label>Vers</label>
                  <div class="location">{{ formatLocation(movement.emplacementDestination) }}</div>
                </div>
                <div class="detail-item" v-if="movement.technicienConcerne || movement.technicien">
                  <label>Technicien</label>
                  <div class="technician">
                    {{ (movement.technicienConcerne || movement.technicien)?.prenom }} {{ (movement.technicienConcerne || movement.technicien)?.nom }}
                  </div>
                </div>
              </div>
              
              <div class="detail-row" v-if="movement.motif">
                <div class="detail-item full-width">
                  <label>Motif</label>
                  <div class="motif">{{ movement.motif }}</div>
                </div>
              </div>
            </div>
            
            <div class="movement-actions" v-if="movement.statut === 'en_attente' || movement.typeMouvement === 'retour_non_consomme'">
              <button v-if="movement.statut === 'en_attente'" @click="validateMovement(movement)" class="btn btn-success btn-sm">
                <i class="fas fa-check"></i>
                Valider
              </button>
              <button v-if="movement.statut === 'en_attente'" @click="cancelMovement(movement)" class="btn btn-danger btn-sm">
                <i class="fas fa-times"></i>
                Annuler
              </button>
              <button v-if="movement.typeMouvement === 'retour_non_consomme'" @click="exportRetourPDF(movement)" class="btn btn-primary btn-sm">
                <i class="fas fa-file-pdf"></i>
                Export PDF
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination" v-if="totalPages > 1">
      <button 
        @click="currentPage = 1" 
        :disabled="currentPage === 1"
        class="pagination-btn"
      >
        <i class="fas fa-angle-double-left"></i>
      </button>
      <button 
        @click="currentPage--" 
        :disabled="currentPage === 1"
        class="pagination-btn"
      >
        <i class="fas fa-angle-left"></i>
      </button>
      
      <span class="pagination-info">
        Page {{ currentPage }} sur {{ totalPages }} 
        ({{ filteredMovements.length }} mouvements)
      </span>
      
      <button 
        @click="currentPage++" 
        :disabled="currentPage === totalPages"
        class="pagination-btn"
      >
        <i class="fas fa-angle-right"></i>
      </button>
      <button 
        @click="currentPage = totalPages" 
        :disabled="currentPage === totalPages"
        class="pagination-btn"
      >
        <i class="fas fa-angle-double-right"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

export default {
  name: 'MovementsManager',
  props: {
    movements: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['refresh', 'create', 'validate', 'cancel'],
  setup(props, { emit }) {
    // État réactif
    const filters = ref({
      typeMouvement: '',
      statut: '',
      dateDebut: '',
      dateFin: ''
    })
    
    const viewMode = ref('timeline') // 'timeline' | 'table'
    
    // Classes dynamiques pour les filtres
    const getTypeMouvementClass = computed(() => {
      return filters.value.typeMouvement ? 'has-selection' : ''
    })
    
    const getStatutClass = computed(() => {
      return filters.value.statut ? 'has-selection' : ''
    })
    
    const getDateDebutClass = computed(() => {
      return filters.value.dateDebut ? 'has-selection' : ''
    })
    
    const getDateFinClass = computed(() => {
      return filters.value.dateFin ? 'has-selection' : ''
    })
    const currentPage = ref(1)
    const itemsPerPage = ref(10)

    // Mouvements filtrés
    const filteredMovements = computed(() => {
      let result = [...props.movements]

      if (filters.value.typeMouvement) {
        result = result.filter(m => m.typeMouvement === filters.value.typeMouvement)
      }
      if (filters.value.statut) {
        result = result.filter(m => m.statut === filters.value.statut)
      }
      if (filters.value.dateDebut) {
        result = result.filter(m => new Date(m.dateHeureMouvement) >= new Date(filters.value.dateDebut))
      }
      if (filters.value.dateFin) {
        result = result.filter(m => new Date(m.dateHeureMouvement) <= new Date(filters.value.dateFin))
      }

      // Tri par date décroissante
      result.sort((a, b) => new Date(b.dateHeureMouvement) - new Date(a.dateHeureMouvement))

      return result
    })

    // Mouvements paginés
    const paginatedMovements = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage.value
      const end = start + itemsPerPage.value
      return filteredMovements.value.slice(start, end)
    })

    // Nombre total de pages
    const totalPages = computed(() => {
      return Math.ceil(filteredMovements.value.length / itemsPerPage.value)
    })

    // Statistiques
    const stats = computed(() => {
      if (!props.movements.length) return null
      
      const entrees = props.movements.filter(m => m.quantite > 0).length
      const sorties = props.movements.filter(m => m.quantite < 0).length
      const enAttente = props.movements.filter(m => m.statut === 'en_attente').length
      const valeurTotale = props.movements.reduce((sum, m) => sum + (m.valeurTotale || 0), 0)
      
      return { entrees, sorties, enAttente, valeurTotale }
    })

    // Méthodes
    const applyFilters = () => {
      currentPage.value = 1
    }

    const getMovementClass = (movement) => {
      const classes = ['movement-item']
      if (movement.quantite > 0) classes.push('movement-in')
      else classes.push('movement-out')
      if (movement.statut === 'en_attente') classes.push('movement-pending')
      return classes.join(' ')
    }

    const getMovementIcon = (type) => {
      const icons = {
        'entree_achat': 'fas fa-truck',
        'sortie_technicien': 'fas fa-user-cog',
        'consommation_client': 'fas fa-user',
        'retour_non_consomme': 'fas fa-undo',
        'retour_defectueux': 'fas fa-exclamation-triangle',
        'inventaire_ajustement': 'fas fa-balance-scale',
        'transfert_interne': 'fas fa-exchange-alt'
      }
      return icons[type] || 'fas fa-box'
    }

    const getMovementTitle = (type) => {
      const titles = {
        'entree_achat': 'Entrée Achat',
        'sortie_technicien': 'Sortie Technicien',
        'consommation_client': 'Consommation Client',
        'retour_non_consomme': 'Retour Non Consommé',
        'retour_defectueux': 'Retour Défectueux',
        'inventaire_ajustement': 'Ajustement Inventaire',
        'transfert_interne': 'Transfert Interne'
      }
      return titles[type] || type
    }

    const getStatusClass = (statut) => {
      const classes = {
        'en_attente': 'status-pending',
        'valide': 'status-validated',
        'annule': 'status-cancelled'
      }
      return classes[statut] || 'status-default'
    }

    const getStatusLabel = (statut) => {
      const labels = {
        'en_attente': 'En attente',
        'valide': 'Validé',
        'annule': 'Annulé'
      }
      return labels[statut] || statut
    }

    const formatLocation = (location, locationNom = null) => {
      // Si on a le nom enrichi du backend, l'utiliser en priorité
      if (locationNom) {
        return locationNom
      }
      
      if (!location) return '-'
      
      // Formats technicien
      if (location.startsWith('sac_technicien_') || 
          location.startsWith('Technicien ') || 
          location.startsWith('Tech:') ||
          location.startsWith('Tech ')) {
        return 'Technicien'
      }
      
      // Formats client
      if (location.startsWith('chez_client_') || 
          location.startsWith('client_') ||
          location.startsWith('Client ') ||
          location.startsWith('Client:')) {
        return 'Chez Client'
      }
      
      // Magasin central
      if (location === 'magasin_central' || location === 'Magasin Central') {
        return 'Magasin Central'
      }
      
      return location.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }

    const formatPrice = (price) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'TND'
      }).format(price || 0)
    }

    const formatDate = (date) => {
      return new Intl.DateTimeFormat('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(date))
    }

    const validateMovement = (movement) => {
      emit('validate', movement)
    }

    const cancelMovement = (movement) => {
      emit('cancel', movement)
    }

    const exportRetourPDF = (movement) => {
      try {
        const doc = new jsPDF()
        const dateNow = new Date()
        
        // EN-TÊTE AMANA (réduit)
        doc.setFontSize(20)
        doc.setTextColor(0, 27, 183)
        doc.setFont('helvetica', 'bold')
        doc.text('AMANA', 105, 15, { align: 'center' })
        
        doc.setFontSize(8)
        doc.setTextColor(100, 100, 100)
        doc.setFont('helvetica', 'normal')
        doc.text('MANAGEMENT SUITE', 105, 21, { align: 'center' })
        
        doc.setDrawColor(0, 27, 183)
        doc.setLineWidth(0.5)
        doc.line(20, 25, 190, 25)

        // TITRE (réduit)
        doc.setFontSize(16)
        doc.setTextColor(0, 27, 183)
        doc.setFont('helvetica', 'bold')
        doc.text('BON DE RETOUR NON CONSOMMÉ', 105, 33, { align: 'center' })

        // Numéro et date (réduit)
        doc.setFontSize(8)
        doc.setTextColor(60, 60, 60)
        doc.setFont('helvetica', 'normal')
        doc.text(`N° ${movement.numeroMouvement}`, 105, 40, { align: 'center' })
        doc.text(`Date: ${formatDate(movement.dateHeureMouvement)}`, 105, 45, { align: 'center' })

        // INFORMATIONS MOUVEMENT (réduit)
        let yPos = 55
        doc.setFillColor(240, 248, 255)
        doc.roundedRect(20, yPos, 170, 28, 3, 3, 'F')
        doc.setDrawColor(0, 27, 183)
        doc.roundedRect(20, yPos, 170, 28, 3, 3, 'S')
        
        doc.setFontSize(9)
        doc.setTextColor(0, 27, 183)
        doc.setFont('helvetica', 'bold')
        doc.text('INFORMATIONS DU RETOUR', 25, yPos + 6)
        
        doc.setFontSize(8)
        doc.setTextColor(40, 40, 40)
        doc.setFont('helvetica', 'normal')
        doc.text(`Statut: ${getStatusLabel(movement.statut)}`, 25, yPos + 13)
        doc.text(`De: ${formatLocation(movement.emplacementOrigine)}`, 25, yPos + 19)
        doc.text(`Vers: ${formatLocation(movement.emplacementDestination)}`, 25, yPos + 25)

        // PRODUIT (réduit)
        yPos = 90
        doc.setFillColor(255, 250, 240)
        doc.roundedRect(20, yPos, 170, 30, 3, 3, 'F')
        doc.setDrawColor(245, 158, 11)
        doc.roundedRect(20, yPos, 170, 30, 3, 3, 'S')
        
        doc.setFontSize(9)
        doc.setTextColor(245, 158, 11)
        doc.setFont('helvetica', 'bold')
        doc.text('PRODUIT RETOURNÉ', 25, yPos + 6)
        
        doc.setFontSize(8)
        doc.setTextColor(40, 40, 40)
        doc.setFont('helvetica', 'bold')
        doc.text(`Référence: ${movement.produit?.referenceInterne || 'N/A'}`, 25, yPos + 13)
        doc.text(`Désignation: ${movement.produit?.designation || 'N/A'}`, 25, yPos + 19)
        doc.text(`Catégorie: ${movement.produit?.categorie || 'N/A'}`, 25, yPos + 25)

        // QUANTITÉ ET VALEUR (réduit)
        yPos = 127
        doc.setFillColor(240, 255, 240)
        doc.roundedRect(20, yPos, 80, 20, 3, 3, 'F')
        doc.setDrawColor(16, 185, 129)
        doc.roundedRect(20, yPos, 80, 20, 3, 3, 'S')
        
        doc.setFontSize(9)
        doc.setTextColor(16, 185, 129)
        doc.setFont('helvetica', 'bold')
        doc.text('QUANTITÉ', 25, yPos + 6)
        
        doc.setFontSize(10)
        doc.setTextColor(40, 40, 40)
        doc.setFont('helvetica', 'bold')
        doc.text(`${Math.abs(movement.quantite)} unité(s)`, 25, yPos + 14)

        // VALEUR
        doc.setFillColor(255, 240, 240)
        doc.roundedRect(110, yPos, 80, 20, 3, 3, 'F')
        doc.setDrawColor(239, 68, 68)
        doc.roundedRect(110, yPos, 80, 20, 3, 3, 'S')
        
        doc.setFontSize(9)
        doc.setTextColor(239, 68, 68)
        doc.setFont('helvetica', 'bold')
        doc.text('VALEUR TOTALE', 115, yPos + 6)
        
        doc.setFontSize(10)
        doc.setTextColor(40, 40, 40)
        doc.setFont('helvetica', 'bold')
        doc.text(`${formatPrice(movement.valeurTotale)} TND`, 115, yPos + 14)

        // MOTIF (réduit)
        yPos = 153
        if (movement.motif) {
          doc.setFontSize(9)
          doc.setTextColor(0, 27, 183)
          doc.setFont('helvetica', 'bold')
          doc.text('MOTIF DU RETOUR:', 20, yPos)
          
          doc.setFontSize(8)
          doc.setTextColor(60, 60, 60)
          doc.setFont('helvetica', 'normal')
          const motifLines = doc.splitTextToSize(movement.motif, 170)
          // Limiter à 3 lignes maximum
          const limitedMotif = motifLines.slice(0, 3)
          doc.text(limitedMotif, 20, yPos + 6)
          yPos += 6 + (limitedMotif.length * 4)
        }

        // TECHNICIEN (réduit)
        if (movement.technicienConcerne) {
          yPos = Math.max(yPos + 8, 170)
          doc.setFillColor(248, 250, 252)
          doc.roundedRect(20, yPos, 170, 16, 3, 3, 'F')
          doc.setDrawColor(100, 116, 139)
          doc.roundedRect(20, yPos, 170, 16, 3, 3, 'S')
          
          doc.setFontSize(8)
          doc.setTextColor(100, 116, 139)
          doc.setFont('helvetica', 'bold')
          doc.text('TECHNICIEN CONCERNÉ:', 25, yPos + 6)
          
          doc.setTextColor(40, 40, 40)
          doc.text(`${movement.technicienConcerne.prenom} ${movement.technicienConcerne.nom}`, 25, yPos + 12)
        }

        // SIGNATURES (réduit et repositionné)
        yPos = Math.max(yPos + 25, 195)

        // Signature magasinier
        doc.setDrawColor(16, 185, 129)
        doc.roundedRect(20, yPos, 75, 35, 3, 3, 'S')
        doc.setFontSize(8)
        doc.setTextColor(16, 185, 129)
        doc.setFont('helvetica', 'bold')
        doc.text('SIGNATURE MAGASINIER', 57.5, yPos + 6, { align: 'center' })
        doc.setFontSize(7)
        doc.setTextColor(100, 100, 100)
        doc.setFont('helvetica', 'normal')
        doc.text('Nom et signature', 57.5, yPos + 30, { align: 'center' })

        // Signature technicien
        doc.setDrawColor(0, 27, 183)
        doc.roundedRect(115, yPos, 75, 35, 3, 3, 'S')
        doc.setFontSize(8)
        doc.setTextColor(0, 27, 183)
        doc.setFont('helvetica', 'bold')
        doc.text('SIGNATURE TECHNICIEN', 152.5, yPos + 6, { align: 'center' })
        doc.setFontSize(7)
        doc.setTextColor(100, 100, 100)
        doc.setFont('helvetica', 'normal')
        doc.text('Nom et signature', 152.5, yPos + 30, { align: 'center' })

        // PIED DE PAGE (réduit et repositionné)
        yPos += 42
        doc.setDrawColor(200, 200, 200)
        doc.line(20, yPos, 190, yPos)
        doc.setFontSize(7)
        doc.setTextColor(120, 120, 120)
        doc.setFont('helvetica', 'italic')
        doc.text('Ce document certifie le retour du produit non consommé au magasin.', 105, yPos + 4, { align: 'center' })
        doc.text('Amana Management Suite - Gestion de Stock', 105, yPos + 9, { align: 'center' })

        // Sauvegarder
        const fileName = `bon_retour_non_consomme_${movement.numeroMouvement}_${dateNow.toISOString().split('T')[0]}.pdf`
        doc.save(fileName)
        
        console.log('✅ Bon de retour non consommé PDF exporté avec succès')
      } catch (error) {
        console.error('❌ Erreur export PDF retour:', error)
        alert(`Erreur lors de l'export PDF: ${error.message}`)
      }
    }

    const exportMovements = () => {
      try {
        // Préparer les données pour l'export
        const exportData = filteredMovements.value.map(movement => ({
          'Date et heure': formatDate(movement.dateHeureMouvement),
          'Type de mouvement': getMovementTitle(movement.typeMouvement),
          'Produit - Référence': movement.produit?.referenceInterne || '',
          'Produit - Désignation': movement.produit?.designation || '',
          'Produit - Catégorie': movement.produit?.categorie || '',
          'Quantité': movement.quantite || 0,
          'Prix unitaire (TND)': movement.prixUnitaire || 0,
          'Valeur totale (TND)': movement.valeurTotale || 0,
          'Emplacement source': formatLocation(movement.emplacementSource || ''),
          'Emplacement destination': formatLocation(movement.emplacementDestination || ''),
          'Statut': getStatusLabel(movement.statut),
          'Utilisateur': movement.utilisateur?.nom ? `${movement.utilisateur.prenom} ${movement.utilisateur.nom}` : '',
          'Commentaires': movement.commentaires || '',
          'Numéro de lot': movement.numeroLot || '',
          'Date d\'expiration': movement.dateExpiration ? new Date(movement.dateExpiration).toLocaleDateString('fr-FR') : '',
          'Fournisseur': movement.fournisseur || '',
          'Bon de commande': movement.bonCommande || '',
          'Facture': movement.numeroFacture || ''
        }))

        // Créer un nouveau workbook
        const wb = XLSX.utils.book_new()
        
        // Créer la feuille de calcul
        const ws = XLSX.utils.json_to_sheet(exportData)
        
        // Définir la largeur des colonnes
        const colWidths = [
          { wch: 20 }, // Date et heure
          { wch: 25 }, // Type de mouvement
          { wch: 18 }, // Produit - Référence
          { wch: 35 }, // Produit - Désignation
          { wch: 15 }, // Produit - Catégorie
          { wch: 12 }, // Quantité
          { wch: 18 }, // Prix unitaire
          { wch: 18 }, // Valeur totale
          { wch: 20 }, // Emplacement source
          { wch: 25 }, // Emplacement destination
          { wch: 15 }, // Statut
          { wch: 20 }, // Utilisateur
          { wch: 30 }, // Commentaires
          { wch: 15 }, // Numéro de lot
          { wch: 18 }, // Date d'expiration
          { wch: 20 }, // Fournisseur
          { wch: 18 }, // Bon de commande
          { wch: 18 }  // Facture
        ]
        ws['!cols'] = colWidths
        
        // Ajouter la feuille au workbook
        XLSX.utils.book_append_sheet(wb, ws, 'Mouvements de Stock')
        
        // Générer le nom du fichier avec la date
        const now = new Date()
        const dateStr = now.toISOString().split('T')[0] // Format YYYY-MM-DD
        const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-') // Format HH-MM-SS
        const filename = `export_mouvements_${dateStr}_${timeStr}.xlsx`
        
        // Télécharger le fichier
        XLSX.writeFile(wb, filename)
        
        // Notification de succès
        console.log(`✅ Export Excel réussi: ${exportData.length} mouvements exportés`)
        
        // Vous pouvez ajouter une notification toast ici si disponible
        // toast.success(`✅ Export Excel réussi: ${exportData.length} mouvements exportés`)
        
      } catch (error) {
        console.error('❌ Erreur lors de l\'export Excel:', error)
        // toast.error('❌ Erreur lors de l\'export Excel')
      }
    }

    const getMovementRowClass = (movement) => {
      const classes = ['movement-row']
      if (movement.quantite > 0) classes.push('movement-row-in')
      else classes.push('movement-row-out')
      if (movement.statut === 'en_attente') classes.push('movement-row-pending')
      return classes.join(' ')
    }

    return {
      // État
      filters,
      viewMode,
      currentPage,
      itemsPerPage,
      
      // Computed
      filteredMovements,
      paginatedMovements,
      totalPages,
      stats,
      
      // Méthodes
      applyFilters,
      getMovementClass,
      getMovementIcon,
      getMovementTitle,
      getStatusClass,
      getStatusLabel,
      formatLocation,
      formatPrice,
      formatDate,
      validateMovement,
      cancelMovement,
      exportRetourPDF,
      exportMovements,
      getMovementRowClass,
      
      // Classes dynamiques
      getTypeMouvementClass,
      getStatutClass,
      getDateDebutClass,
      getDateFinClass
    }
  }
}
</script>

<style scoped>
.movements-manager {
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 27, 183, 0.1);
  overflow: hidden;
  border: 1px solid rgba(0, 27, 183, 0.1);
}

/* Barre d'outils */
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
  border-bottom: 1px solid rgba(0, 27, 183, 0.1);
  flex-wrap: wrap;
  gap: 1.5rem;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.filters {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
}

/* Design professionnel pour les filtres de mouvements */
.filter-wrapper, .date-filter-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 251, 255, 0.95) 100%);
  border: 2px solid rgba(0, 27, 183, 0.15);
  border-radius: 16px;
  padding: 0;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 27, 183, 0.08);
  min-width: 200px;
}

.date-filter-wrapper {
  min-width: 180px;
}

.filter-wrapper:hover, .date-filter-wrapper:hover {
  border-color: rgba(0, 27, 183, 0.3);
  box-shadow: 0 8px 32px rgba(0, 27, 183, 0.15);
  transform: translateY(-2px);
}

.filter-wrapper:focus-within, .date-filter-wrapper:focus-within {
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 4px rgba(0, 27, 183, 0.1), 0 8px 32px rgba(0, 27, 183, 0.2);
  transform: translateY(-3px);
}

.filter-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
  font-size: 0.9rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.filter-icon::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.2), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.filter-wrapper:hover .filter-icon::before,
.date-filter-wrapper:hover .filter-icon::before {
  opacity: 1;
}

.premium-select, .premium-date {
  flex: 1;
  padding: 12px 16px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: var(--primary-blue);
  cursor: pointer;
  outline: none;
  letter-spacing: 0.3px;
  transition: all 0.3s ease;
}

.premium-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.premium-date {
  color: var(--primary-blue);
  font-weight: 600;
}

.premium-select:focus, .premium-date:focus {
  color: var(--secondary-blue);
}

.premium-select option {
  background: white;
  color: #2c3e50;
  padding: 12px 16px;
  font-weight: 500;
  border-radius: 8px;
  margin: 2px 0;
}

.premium-select option:hover {
  background: linear-gradient(135deg, #f8fbff 0%, #e3f2fd 100%);
  color: var(--primary-blue);
}

.premium-select option[value=""] {
  font-weight: 700;
  color: var(--primary-blue);
  background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
}

.filter-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 40px;
  color: var(--primary-blue);
  font-size: 0.8rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
}

.filter-wrapper:hover .filter-arrow {
  color: var(--secondary-blue);
  transform: translateY(-1px);
}

.filter-wrapper:focus-within .filter-arrow {
  transform: rotate(180deg);
}

/* Indicateur de sélection active pour mouvements */
.filter-wrapper.has-selection, .date-filter-wrapper.has-selection {
  border-color: var(--primary-blue);
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.08) 0%, rgba(0, 70, 255, 0.08) 100%);
  box-shadow: 0 4px 20px rgba(0, 27, 183, 0.15), 0 0 0 1px rgba(0, 27, 183, 0.1);
}

.filter-wrapper.has-selection .filter-icon,
.date-filter-wrapper.has-selection .filter-icon {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  animation: pulse 2s infinite;
  box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.3);
}

.filter-wrapper.has-selection .premium-select,
.date-filter-wrapper.has-selection .premium-date {
  color: var(--secondary-blue);
  font-weight: 700;
}

.filter-wrapper.has-selection .filter-arrow {
  color: var(--secondary-blue);
}

/* Badge de compteur pour les filtres actifs */
.filter-wrapper.has-selection::after,
.date-filter-wrapper.has-selection::after {
  content: '1';
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
  color: white;
  border-radius: 50%;
  font-size: 0.7rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(255, 107, 107, 0.4);
  animation: bounceIn 0.5s ease-out;
}

@keyframes bounceIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Effet de brillance au hover */
.filter-wrapper:hover::after,
.date-filter-wrapper:hover::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
  animation: shine 1.5s ease-in-out;
  pointer-events: none;
  z-index: 1;
}

@keyframes shine {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

/* Responsive pour les filtres de mouvements */
@media (max-width: 768px) {
  .filters {
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
  }
  
  .filter-wrapper, .date-filter-wrapper {
    min-width: auto;
    width: 100%;
  }
  
  .premium-select, .premium-date {
    font-size: 13px;
  }
  
  .filter-icon {
    width: 35px;
    height: 35px;
    font-size: 0.8rem;
  }
  
  .filter-arrow {
    width: 28px;
    height: 35px;
  }
}

.toolbar-right {
  display: flex;
  gap: 0.5rem;
}

/* Statistiques rapides */
.quick-stats {
  display: flex;
  padding: 2rem;
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  color: white;
  gap: 3rem;
  flex-wrap: wrap;
  position: relative;
  overflow: hidden;
}

.quick-stats::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1), transparent);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  min-width: 220px;
  position: relative;
  z-index: 1;
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  position: relative;
  overflow: hidden;
}

.stat-icon::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.2), transparent);
  border-radius: 16px;
}

.stat-icon-blue { 
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  box-shadow: 0 8px 24px rgba(0, 27, 183, 0.3);
}
.stat-icon-red { 
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  box-shadow: 0 8px 24px rgba(0, 27, 183, 0.3);
}
.stat-icon-orange { 
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  box-shadow: 0 8px 24px rgba(0, 27, 183, 0.3);
}
.stat-icon-green { 
  background: linear-gradient(135deg, var(--secondary-blue), #42a5f5);
  box-shadow: 0 8px 24px rgba(0, 70, 255, 0.3);
}

.stat-content {
  color: white;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.stat-label {
  font-size: 0.9rem;
  opacity: 0.9;
  margin-top: 0.5rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Timeline */
.movements-timeline {
  position: relative;
  padding: 2rem;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: #007bff;
}

.timeline {
  position: relative;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 25px;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(to bottom, var(--primary-blue), var(--secondary-blue), #42a5f5);
  border-radius: 2px;
}

.timeline-item {
  position: relative;
  margin-bottom: 2rem;
  padding-left: 70px;
}

.timeline-marker {
  position: absolute;
  left: 0;
  top: 0;
  width: 50px;
  height: 50px;
  background: white;
  border: 3px solid var(--primary-blue);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: var(--primary-blue);
  z-index: 2;
  box-shadow: 0 4px 16px rgba(0, 27, 183, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.movement-item.movement-in .timeline-marker {
  border-color: var(--secondary-blue);
  color: var(--secondary-blue);
  background: linear-gradient(135deg, #e3f2fd, #ffffff);
}

.movement-item.movement-out .timeline-marker {
  border-color: var(--primary-blue);
  color: var(--primary-blue);
  background: linear-gradient(135deg, #e8eaf6, #ffffff);
}

.movement-item.movement-pending .timeline-marker {
  border-color: var(--secondary-blue);
  color: var(--secondary-blue);
  background: linear-gradient(135deg, #fff3e0, #ffffff);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

.timeline-content {
  background: white;
  border: 1px solid rgba(0, 27, 183, 0.1);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 8px 32px rgba(0, 27, 183, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.timeline-content::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
}

.timeline-content:hover {
  box-shadow: 0 20px 60px rgba(0, 27, 183, 0.2);
  transform: translateY(-8px);
  border-color: rgba(0, 27, 183, 0.2);
}

.movement-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.movement-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.movement-title {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--primary-blue);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.movement-number {
  font-family: 'Courier New', monospace;
  background: linear-gradient(135deg, #f8fbff, #e3f2fd);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  color: var(--primary-blue);
  font-weight: 600;
  border: 1px solid rgba(0, 27, 183, 0.2);
}

.movement-meta {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.movement-date {
  color: #6c757d;
  font-size: 0.9rem;
}

.movement-status {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid;
}

.status-pending { 
  background: linear-gradient(135deg, #fff8e1, #ffecb3); 
  color: #f57f17; 
  border-color: rgba(245, 127, 23, 0.3);
}
.status-validated { 
  background: linear-gradient(135deg, #e8f5e8, #c8e6c9); 
  color: #2e7d32; 
  border-color: rgba(46, 125, 50, 0.3);
}
.status-cancelled { 
  background: linear-gradient(135deg, #ffebee, #ffcdd2); 
  color: #d32f2f; 
  border-color: rgba(211, 47, 47, 0.3);
}

.movement-details {
  margin-bottom: 1rem;
}

.detail-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.detail-row:last-child {
  margin-bottom: 0;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.detail-item.full-width {
  grid-column: 1 / -1;
}

.detail-item label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.product-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.product-info strong {
  color: var(--primary-blue);
  font-family: 'Courier New', monospace;
  font-weight: 700;
}

.product-info span {
  color: #495057;
  font-size: 0.9rem;
}

.quantity {
  font-weight: 600;
  font-size: 1.1rem;
}

.quantity.positive {
  color: var(--secondary-blue);
  background: linear-gradient(135deg, #e3f2fd, #ffffff);
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  border: 1px solid rgba(0, 70, 255, 0.2);
}

.quantity.negative {
  color: var(--primary-blue);
  background: linear-gradient(135deg, #e8eaf6, #ffffff);
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  border: 1px solid rgba(0, 27, 183, 0.2);
}

.value {
  font-weight: 600;
  color: var(--secondary-blue);
  background: linear-gradient(135deg, #e3f2fd, #ffffff);
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  border: 1px solid rgba(0, 70, 255, 0.2);
}

.location {
  color: #495057;
  font-size: 0.9rem;
}

.technician {
  color: var(--primary-blue);
  font-weight: 600;
  background: linear-gradient(135deg, #f8fbff, #ffffff);
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  border: 1px solid rgba(0, 27, 183, 0.2);
}

.motif {
  color: #495057;
  font-style: italic;
  line-height: 1.4;
}

.movement-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

/* Boutons */
.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  text-decoration: none;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}

.btn-primary { 
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue)); 
  color: white;
  border: 1px solid rgba(0, 27, 183, 0.3);
}
.btn-secondary { 
  background: linear-gradient(135deg, #6c757d, #495057); 
  color: white;
  border: 1px solid rgba(108, 117, 125, 0.3);
}
.btn-success { 
  background: linear-gradient(135deg, var(--secondary-blue), #42a5f5); 
  color: white;
  border: 1px solid rgba(0, 70, 255, 0.3);
}
.btn-danger { 
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue)); 
  color: white;
  border: 1px solid rgba(0, 27, 183, 0.3);
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* Message aucune donnée */
.no-data {
  text-align: center;
  padding: 4rem 2rem;
  color: #6c757d;
}

.no-data i {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.no-data h3 {
  margin-bottom: 0.5rem;
  color: #495057;
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  gap: 0.5rem;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.pagination-btn {
  width: 40px;
  height: 40px;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  margin: 0 1rem;
  font-size: 0.9rem;
  color: #6c757d;
}

/* Styles spécifiques pour le filtre des types de mouvements (Orange) */
.filter-movement-type {
  border-color: rgba(255, 152, 0, 0.15);
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.05) 0%, rgba(255, 193, 7, 0.05) 100%);
}

.filter-movement-type:hover {
  border-color: rgba(255, 152, 0, 0.3);
  box-shadow: 0 8px 32px rgba(255, 152, 0, 0.15);
}

.filter-movement-type:focus-within {
  border-color: #ff9800;
  box-shadow: 0 0 0 4px rgba(255, 152, 0, 0.1), 0 8px 32px rgba(255, 152, 0, 0.2);
}

.filter-movement-type .filter-icon {
  background: linear-gradient(135deg, #ff9800 0%, #ffc107 100%);
}

.filter-movement-type .premium-select {
  color: #ff9800;
}

.filter-movement-type .premium-select:focus {
  color: #f57c00;
}

.filter-movement-type .filter-arrow {
  color: #ff9800;
}

.filter-movement-type:hover .filter-arrow {
  color: #f57c00;
}

.filter-movement-type.has-selection {
  border-color: #ff9800;
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.08) 0%, rgba(255, 193, 7, 0.08) 100%);
  box-shadow: 0 4px 20px rgba(255, 152, 0, 0.15), 0 0 0 1px rgba(255, 152, 0, 0.1);
}

.filter-movement-type.has-selection .premium-select {
  color: #f57c00;
}

.filter-movement-type.has-selection .filter-arrow {
  color: #f57c00;
}

/* Styles spécifiques pour le filtre des statuts de mouvements (Violet) */
.filter-movement-status {
  border-color: rgba(156, 39, 176, 0.15);
  background: linear-gradient(135deg, rgba(156, 39, 176, 0.05) 0%, rgba(142, 36, 170, 0.05) 100%);
}

.filter-movement-status:hover {
  border-color: rgba(156, 39, 176, 0.3);
  box-shadow: 0 8px 32px rgba(156, 39, 176, 0.15);
}

.filter-movement-status:focus-within {
  border-color: #9c27b0;
  box-shadow: 0 0 0 4px rgba(156, 39, 176, 0.1), 0 8px 32px rgba(156, 39, 176, 0.2);
}

.filter-movement-status .filter-icon {
  background: linear-gradient(135deg, #9c27b0 0%, #8e24aa 100%);
}

.filter-movement-status .premium-select {
  color: #9c27b0;
}

.filter-movement-status .premium-select:focus {
  color: #7b1fa2;
}

.filter-movement-status .filter-arrow {
  color: #9c27b0;
}

.filter-movement-status:hover .filter-arrow {
  color: #7b1fa2;
}

.filter-movement-status.has-selection {
  border-color: #9c27b0;
  background: linear-gradient(135deg, rgba(156, 39, 176, 0.08) 0%, rgba(142, 36, 170, 0.08) 100%);
  box-shadow: 0 4px 20px rgba(156, 39, 176, 0.15), 0 0 0 1px rgba(156, 39, 176, 0.1);
}

.filter-movement-status.has-selection .premium-select {
  color: #7b1fa2;
}

.filter-movement-status.has-selection .filter-arrow {
  color: #7b1fa2;
}

/* Styles spécifiques pour le filtre de date de début (Bleu) */
.filter-date-start {
  border-color: rgba(59, 130, 246, 0.15);
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(37, 99, 235, 0.05) 100%);
}

.filter-date-start:hover {
  border-color: rgba(59, 130, 246, 0.3);
  box-shadow: 0 8px 32px rgba(59, 130, 246, 0.15);
}

.filter-date-start:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1), 0 8px 32px rgba(59, 130, 246, 0.2);
}

.filter-date-start .filter-icon {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.filter-date-start .premium-date {
  color: #3b82f6;
}

.filter-date-start .premium-date:focus {
  color: #2563eb;
}

.filter-date-start.has-selection {
  border-color: #3b82f6;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(37, 99, 235, 0.08) 100%);
  box-shadow: 0 4px 20px rgba(59, 130, 246, 0.15), 0 0 0 1px rgba(59, 130, 246, 0.1);
}

.filter-date-start.has-selection .premium-date {
  color: #2563eb;
}

/* Styles spécifiques pour le filtre de date de fin (Rouge) */
.filter-date-end {
  border-color: rgba(239, 68, 68, 0.15);
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(220, 38, 38, 0.05) 100%);
}

.filter-date-end:hover {
  border-color: rgba(239, 68, 68, 0.3);
  box-shadow: 0 8px 32px rgba(239, 68, 68, 0.15);
}

.filter-date-end:focus-within {
  border-color: #ef4444;
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1), 0 8px 32px rgba(239, 68, 68, 0.2);
}

.filter-date-end .filter-icon {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.filter-date-end .premium-date {
  color: #ef4444;
}

.filter-date-end .premium-date:focus {
  color: #dc2626;
}

.filter-date-end.has-selection {
  border-color: #ef4444;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.08) 100%);
  box-shadow: 0 4px 20px rgba(239, 68, 68, 0.15), 0 0 0 1px rgba(239, 68, 68, 0.1);
}

.filter-date-end.has-selection .premium-date {
  color: #dc2626;
}

/* Style pour le bouton d'export des mouvements (Vert) */
.btn-export-movements {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  border: 1px solid #28a745;
  position: relative;
  overflow: hidden;
}

.btn-export-movements::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  transition: left 0.5s ease;
}

.btn-export-movements:hover::before {
  left: 100%;
}

.btn-export-movements:hover {
  background: linear-gradient(135deg, #20c997 0%, #17a2b8 100%);
  border-color: #20c997;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4);
}

.view-toggle {
  display: flex;
  gap: 0.5rem;
  margin-left: 1rem;
}

.toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.9rem;
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  background: linear-gradient(135deg, rgba(255,255,255,0.9), rgba(248,250,252,0.85));
  color: #1f2937;
  cursor: pointer;
  transition: all 0.25s ease;
}

.toggle-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(0,0,0,0.08); }
.toggle-btn.active { 
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue)); 
  color: #fff; 
  box-shadow: 0 6px 18px rgba(0, 27, 183, 0.25);
  border-color: transparent;
}

.movements-table-wrapper {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  position: relative;
}

.table-scroll { overflow-x: auto; }

.movements-table {
  width: 100%;
  min-width: 1200px;
  border-collapse: separate;
  border-spacing: 0;
}

.movements-table thead th {
  position: sticky;
  top: 0;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.8), rgba(241, 245, 249, 0.6));
  backdrop-filter: blur(6px);
  text-align: left;
  font-size: 0.8rem;
  letter-spacing: 0.3px;
  color: #374151;
  padding: 0.75rem;
  border-bottom: 1px solid rgba(226,232,240,0.9);
}

.movements-table tbody td {
  padding: 0.75rem;
  font-size: 0.9rem;
  color: #374151;
  border-bottom: 1px solid rgba(226,232,240,0.6);
  vertical-align: top;
}

.movements-table tbody tr:hover { background: rgba(248, 250, 252, 0.6); }

.movement-row-in { box-shadow: inset 4px 0 0 var(--secondary-blue); }
.movement-row-out { box-shadow: inset 4px 0 0 var(--primary-blue); }
.movement-row-pending { box-shadow: inset 4px 0 0 #f59e0b; }

.movement-number-cell .movement-number {
  font-family: 'Courier New', monospace;
  background: linear-gradient(135deg, #f8fbff, #e3f2fd);
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.8rem;
  color: var(--primary-blue);
  font-weight: 600;
  border: 1px solid rgba(0, 27, 183, 0.2);
}

.date-cell { font-size: 0.85rem; color: #6b7280; }

.type-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--primary-blue);
}

.product-cell .product-ref {
  font-family: 'Courier New', monospace;
  font-weight: 700;
  color: var(--primary-blue);
  font-size: 0.85rem;
}

.product-cell .product-name {
  color: #6b7280;
  font-size: 0.8rem;
  margin-top: 0.2rem;
}

.quantity-cell {
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  text-align: center;
  min-width: 60px;
}

.quantity-cell.positive {
  color: var(--secondary-blue);
  background: linear-gradient(135deg, #e3f2fd, #ffffff);
  border: 1px solid rgba(0, 70, 255, 0.2);
}

.quantity-cell.negative {
  color: var(--primary-blue);
  background: linear-gradient(135deg, #e8eaf6, #ffffff);
  border: 1px solid rgba(0, 27, 183, 0.2);
}

.value-cell {
  font-weight: 600;
  color: var(--secondary-blue);
  background: linear-gradient(135deg, #e3f2fd, #ffffff);
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  border: 1px solid rgba(0, 70, 255, 0.2);
  font-size: 0.85rem;
}

.location-cell {
  color: #495057;
  font-size: 0.85rem;
}

.technician-cell {
  color: var(--primary-blue);
  font-weight: 600;
  background: linear-gradient(135deg, #f8fbff, #ffffff);
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  border: 1px solid rgba(0, 27, 183, 0.2);
  font-size: 0.8rem;
}

.row-actions { display: flex; gap: 0.4rem; }
.muted { color: #6b7280; font-size: 0.85rem; }

/* Responsive */
@media (max-width: 768px) {
  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }
  
  .quick-stats {
    flex-direction: column;
    gap: 1rem;
  }
  
  .stat-item {
    min-width: auto;
  }
  
  .timeline::before {
    left: 15px;
  }
  
  .timeline-item {
    padding-left: 50px;
  }
  
  .timeline-marker {
    width: 30px;
    height: 30px;
    font-size: 1rem;
  }
  
  .movement-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .detail-row {
    grid-template-columns: 1fr;
  }
  
  .movement-actions {
    flex-direction: column;
  }
  
  .view-toggle {
    margin-left: 0;
    margin-top: 1rem;
  }
  
  .movements-table { min-width: 800px; }
}
</style>
